/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { getCatalogId, getUrlSync, ICoreContext } from '@msdyn365-commerce/core';
import {
    ProductDimension,
    ProductDimensionAvailabilitySearchCriteria,
    ProductPrice,
    ProductSearchResult
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    DimensionAvailabilitiesForProductSearchResultInput,
    DimensionTypes,
    DimensionSwatchDisplayTypes,
    getDimensionAvailabilitiesForProductSearchResultAction,
    IDimensionsApp,
    ObjectExtensions,
    PriceForProductSearchResultInput,
    validateCatalogId,
    createInventoryAvailabilitySearchCriteria,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getTelemetryObject,
    IComponentNodeProps,
    IModuleProps,
    INodeProps,
    ISingleSlideCarouselProps,
    ITelemetryContent,
    NodeTag,
    SingleSlideCarousel
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IExtProductCollectionData } from './ext-product-collection.data';
import { IExtProductCollectionProps, layout } from './ext-product-collection.props.autogenerated';

import { ProductComponent } from '../../shared/components/product.component';
import { IPriceComponentResources } from '../../shared/components/price.component';

export interface IProductCollectionViewProps extends IExtProductCollectionProps<IExtProductCollectionData> {
    heading?: React.ReactNode;
    ProductCollectionContainer: IModuleProps;
    SingleSlideCarouselComponentProps: INodeProps;
    GridComponentProps: INodeProps;
    products: IProductComponentViewProps[];
    isCarousel: boolean;
    productPrices?: ProductPrice[];
    seeAllButton?: React.ReactNode;
}

export interface IProductComponentViewProps {
    ProductContainer: INodeProps;
    productComponent: React.ReactNode;
}
export interface IProductComponentState {
    products: ProductSearchResultExpanded[];
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    lastUsedGender?: GenderCookieValue | undefined;
}

import { getCommercialName } from '../../shared/functions/get-commercial-name';
import { getPriceForProductSearchResultAction } from '../../actions/get-price-for-product-search-result';
import { ProductSearchResultExpanded } from '../../shared/classes/ProductSearchResultExpanded';
import { getDimensionValuesWithEstimatedAvailabilitiesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getCookieValueFromContext, checkIfFunctionalConsentGiven } from '../../shared/functions/cookie';
import { GENDER_COOKIE_NAME, GenderCookieValue } from '../ext-header/components/ext-header-helper';

/**
 * ProductCollection component.
 */
class ProductCollection extends React.PureComponent<IExtProductCollectionProps<IExtProductCollectionData>, IProductComponentState> {
    private readonly telemetryContent: ITelemetryContent;

    private readonly productPrices: ProductPrice[];

    constructor(props: IExtProductCollectionProps<IExtProductCollectionData>) {
        super(props);
        this.productPrices = [];
        const genderCookieValue = getCookieValueFromContext<GenderCookieValue>(GENDER_COOKIE_NAME, this.props.context);
        this.state = {
            products: this.getProductList(genderCookieValue),
            lastUsedGender: genderCookieValue
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public async componentDidMount(): Promise<void> {
        if (this.props.config.enableAffiliationBasedPricing) {
            await this._getActivePrice();
        }
        const genderCookieValue = getCookieValueFromContext<GenderCookieValue>(GENDER_COOKIE_NAME, this.props.context);
        this.setState({
            products: this.getProductList(genderCookieValue),
            lastUsedGender: genderCookieValue
        });
        window.addEventListener('ucEvent', this.resetListIfCookieConsentIsRevoked);
    }

    public componentWillUnmount() {
        window.removeEventListener('ucEvent', this.resetListIfCookieConsentIsRevoked);
    }

    public async componentDidUpdate(): Promise<void> {
        if (this.props.app.config.hideProductsWithoutImages) {
            document.querySelectorAll('.msc-empty_image').forEach(item => {
                var li = item.closest('li');
                if (li !== null) {
                    //TODO: should be information telemetry
                    this.props.telemetry.warning('Product without image was filtered out', li.querySelector('a')?.href);
                    li.style.display = 'none';
                }
            });
        }
    }

    public render(): JSX.Element | null {
        const { heading, className, shouldDisplaySeeAllButton } = this.props.config;
        const { resources } = this.props;
        const recommendation = this.props.config.productCollection.recommendationListId;

        // Initialize state in case if the products were not found during initial module render
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Initialize data if not there.
        this.initProductState();

        const products = this.state.products;
        if (this.props.app.config?.useCommercialStyleName) {
            for (var product of products) {
                product.Name = getCommercialName(
                    product.AttributeValues,
                    product.Name,
                    this.props.app.config?.commercialStyleNameAttribute
                );
            }
        }
        const isCarousel = this.props.config.layout ? this.props.config.layout === layout.carousel : true;
        const headingLabel = heading ? heading.text : '';
        const headingComponent = heading && (
            <Msdyn365.Text
                className='ms-product-collection__heading'
                tag={heading.tag || 'h1'}
                text={headingLabel}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- product check for safety.
        const updatedProducts = products?.map((item: ProductSearchResult, index: number) => {
            const getProductData = this._getProduct(item, index);
            return !ObjectExtensions.isNullOrUndefined(getProductData) ? getProductData : undefined;
        });

        const productCollectionViewProps = {
            ...this.props,
            heading: headingComponent,
            SingleSlideCarouselComponentProps: {
                tag: SingleSlideCarousel,
                className: '',
                flipperPrevLabel: `${headingLabel} ${resources.flipperPrevious}`,
                flipperNextLabel: `${headingLabel} ${resources.flipperNext}`,
                parentId: this.props.id,
                telemetryContent: this.telemetryContent
            } as IComponentNodeProps<ISingleSlideCarouselProps>,
            ProductCollectionContainer: {
                moduleProps: this.props,
                className: classnames('ms-product-collection', className)
            },
            GridComponentProps: {
                tag: 'ul' as NodeTag,
                className: 'ms-product-collection__items'
            },
            isCarousel,

            products:
                ArrayExtensions.hasElements(updatedProducts) &&
                updatedProducts.filter(item => {
                    return item !== undefined;
                }),
            productPrices: this.productPrices,
            seeAllButton:
                recommendation && shouldDisplaySeeAllButton && ArrayExtensions.hasElements(updatedProducts)
                    ? this._getSeeAllButton(recommendation, headingLabel)
                    : undefined
        };
        return this.props.renderView(productCollectionViewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    private getProductList(genderCookieValue: GenderCookieValue | undefined) {
        return (
            (genderCookieValue === 'male' && this.props.config.productCollectionMale
                ? this.props.config.productCollectionMale?.products
                : this.props.config.productCollection.products) || []
        );
    }

    private resetListIfCookieConsentIsRevoked = async () => {
        const functionalConsent = await checkIfFunctionalConsentGiven();
        if (!functionalConsent) {
            this.setState({
                products: this.getProductList(undefined),
                lastUsedGender: undefined
            });
        }
    };

    /**
     * Get products dimension availabilities.
     * @param products - Products.
     * @returns Promise.
     */
    private readonly _getProductsDimensionsAvailabilities = async (products: ProductSearchResult[]): Promise<void> => {
        if (!this.props.config.enableAvailabilityCheck) {
            return;
        }

        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = context.app.config.dimensionsInProductCard;
        const dimensionsToDisplayAsSwatch = context.app.config.dimensionsAsSwatchType;
        const validProducts = ArrayExtensions.validValues(products);
        if (
            ArrayExtensions.hasElements(validProducts) &&
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            ArrayExtensions.hasElements(dimensionsToDisplayAsSwatch) &&
            !dimensionsToDisplayAsSwatch.includes(DimensionTypes.none)
        ) {
            const productsDimensionAvailabilities = await getDimensionAvailabilitiesForProductSearchResultAction(
                new DimensionAvailabilitiesForProductSearchResultInput(
                    validProducts,
                    this.props.context as ICoreContext<IDimensionsApp>,
                    DimensionSwatchDisplayTypes.productCard
                ),
                this.props.context.actionContext
            );

            if (ArrayExtensions.hasElements(productsDimensionAvailabilities)) {
                this.setState({ productsDimensionAvailabilities });
            }
        }
    };

    /**
     * Helper method to initialize products state.
     * @param productCollection - List of products.
     */
    private readonly initProductState = async () => {
        let products = this.state.products;
        const genderCookieValue = getCookieValueFromContext<GenderCookieValue>(GENDER_COOKIE_NAME, this.props.context);
        const productCollectionProducts = this.getProductList(genderCookieValue);
        if (!ArrayExtensions.hasElements(products) && ArrayExtensions.hasElements(productCollectionProducts)) {
            products = productCollectionProducts;
            this.setState({ products });
        } else if (productCollectionProducts === undefined && ArrayExtensions.hasElements(this.state.products)) {
            products = [];
            this.setState({ products: products });
        }

        if (Msdyn365.msdyn365Commerce.isBrowser && !ArrayExtensions.hasElements(this.state.productsDimensionAvailabilities)) {
            await this._getProductsDimensionsAvailabilities(products);
        }
    };

    private readonly _getProduct = (product: ProductSearchResultExpanded, index: number): IProductComponentViewProps | null => {
        if (!product || !product.Name) {
            this.props.telemetry.trace('[product-collection] Product or product name is null');
            return null;
        }
        const telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const { imageSettings, allowBackNavigation } = this.props.config;

        // BS - default image settings for mobile
        if (imageSettings && !imageSettings?.viewports) {
            imageSettings.viewports = {
                xs: { q: 'w=295&h=393&m=6', w: 0, h: 0 }
            };
        }
        const resources = this.props.resources;
        const breaCrumbType = this.props.context.app.config.breadcrumbType;
        const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');
        const quickviewSlot =
            this.props.slots && this.props.slots.quickview && this.props.slots.quickview.length > 0 ? this.props.slots.quickview[0] : null;
        const productComparisonButtonSlot = ArrayExtensions.hasElements(this.props.slots.productComparisonButton)
            ? this.props.slots.productComparisonButton[0]
            : null;

        const reviewCount = 1;
        const reviewTotalRatings = Number(product.TotalRatings);
        const reviewCountAriaLabelValue =
            reviewTotalRatings > reviewCount ? resources.ratingCountAriaLabel : resources.ratingCountAriaLabelSingleUser;
        const isPriceMinMaxEnabled = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );
        const priceResources: IPriceComponentResources = {
            priceRangeSeparator: resources.priceRangeSeparator,
            memberPriceMessage: resources.memberPriceMessage,
            saleMessage: resources.saleMessage
        };
        return {
            ProductContainer: {
                tag: 'li' as NodeTag,
                className: 'ms-product-collection__item',
                role: 'listitem',
                key: index
            },
            productComponent: (
                <ProductComponent
                    context={this.props.context}
                    telemetryContent={telemetryContent}
                    imageSettings={imageSettings}
                    freePriceText={resources.priceFree}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    ratingAriaLabel={resources.ratingAriaLabel}
                    ratingCountAriaLabel={reviewCountAriaLabelValue}
                    allowBack={allowBack}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{ product }}
                    quickViewButton={quickviewSlot}
                    productComparisonButton={productComparisonButtonSlot}
                    isPriceMinMaxEnabled={isPriceMinMaxEnabled?.IsEnabled}
                    priceResources={priceResources}
                    dimensionAvailabilities={this.state.productsDimensionAvailabilities?.find(
                        dimensionAvailability =>
                            ArrayExtensions.hasElements(dimensionAvailability) &&
                            dimensionAvailability[0].masterProductId === (product.MasterProductId ?? product.RecordId)
                    )}
                />
            )
        };
    };

    /**
     * Get the active price for products.
     */
    private readonly _getActivePrice = async (): Promise<void> => {
        const productsList = this.state.products;
        //BS CUSTOM - get the record id for the color variant in order to display discounts for specific variants
        const productPromises: Promise<{ product: ProductSearchResultExpanded; dimensionValues: any }>[] = [];
        for (const product of productsList) {
            const colorAttribute = product.AttributeValues?.find(attribute => attribute.Name?.toLowerCase() === 'color');

            const dimensions: ProductDimension[] = [
                {
                    DimensionTypeValue: 1,
                    DimensionValue: {
                        RecordId: colorAttribute?.RecordId ?? 0,
                        Value: colorAttribute?.TextValue?.split('|')[0]
                    }
                }
            ];

            const shippingInventoryConfiguration = createInventoryAvailabilitySearchCriteria(
                this.props.context.actionContext,
                [],
                true,
                undefined,
                undefined
            );

            const searchCriteria: ProductDimensionAvailabilitySearchCriteria = {
                RequestedDimensionTypeValue: 1,
                MatchingDimensionValues: dimensions,
                DefaultWarehouseOnly: shippingInventoryConfiguration.DefaultWarehouseOnly,
                FilterByChannelFulfillmentGroup: shippingInventoryConfiguration.FilterByChannelFulfillmentGroup,
                DeliveryModeTypeFilterValue: shippingInventoryConfiguration.DeliveryModeTypeFilterValue,
                CatalogId: this.props.context.request.apiSettings.catalogId
            };

            // Fire the async call, but return a promise that also includes the original product
            const promise = getDimensionValuesWithEstimatedAvailabilitiesAsync(
                {
                    callerContext: this.props.context.actionContext,
                    queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(this.props.context.actionContext)
                },
                product.MasterProductId ? product.MasterProductId : product.RecordId,
                searchCriteria
            ).then(dimensionValues => ({
                product,
                dimensionValues
            }));

            productPromises.push(promise); // Collect the enriched promise
        }
        // Wait for all the promises at once
        const productsWithValues = await Promise.all(productPromises);
        // Now act on each resolved pair
        for (const { product, dimensionValues } of productsWithValues) {
            if (dimensionValues?.length > 0 && dimensionValues[0].ProductIds) {
                product.RecordId = dimensionValues[0].ProductIds[0];
            }
        }

        //Uses custom action
        let products = await getPriceForProductSearchResultAction(
            new PriceForProductSearchResultInput(productsList),
            this.props.context.actionContext
        );
        //remap the products recordId back to masterId
        products = products.map(product => {
            return { ...product, RecordId: product.MasterProductId ?? 0 };
        });
        if (Msdyn365.msdyn365Commerce.isBrowser) {
            await this._getProductsDimensionsAvailabilities(products);
        }

        this.setState({ products });
    };

    /**
     * Get the see all button.
     * @param recommendation - Recommendation.
     * @param headingLabel - Heading label.
     * @returns IProductComponentSeeAllViewProps - Product component see all view props.
     */
    private readonly _getSeeAllButton = (recommendation: string, headingLabel: string): React.ReactNode => {
        if (!Msdyn365.msdyn365Commerce.isBrowser) {
            return null;
        }

        const catalogId = getCatalogId(this.props.context.request);
        validateCatalogId(catalogId);
        const searchPageLink = getUrlSync('search', this.props.context.actionContext);
        if (!searchPageLink) {
            return null;
        }

        const url = new URL(searchPageLink, window.location.href);
        url.searchParams.set('recommendation', recommendation);
        url.searchParams.set('title', headingLabel);
        url.searchParams.set('catalogid', `${catalogId}`);

        return (
            <Button
                className='ms-product-collection__see-all-btn msc-cta__primary'
                title={this.props.resources.seeAllButtonText}
                href={url.href}
            >
                {this.props.resources.seeAllButtonText}
            </Button>
        );
    };
}

export default ProductCollection;
